import { useMemo } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { FormattedMessage } from "react-intl";
import { LuCheck } from "react-icons/lu";
import {
  getCourse,
  getCourseSlides,
  getCourseStatus,
  isNestedNumber,
  isSubtopicComplete,
  subtopicNumbers,
  subtopicSlidePrefix,
  topicNumber,
} from "@/lib/data/courses";
import { useCourseProgressStore } from "@/lib/stores/courseProgressStore";
import { useLocale } from "@/contexts/IntlContext";
import { pickLocalized } from "@/lib/utils/localized";
import { Button } from "@/components/ui/button";
import { CourseStatusChip } from "@/components/trainer/CourseStatusChip";

export const Route = createFileRoute("/trainer/courses/$courseSlug/")({
  component: CourseOverview,
});

function CourseOverview() {
  const { courseSlug } = Route.useParams();
  const navigate = useNavigate();
  const { locale } = useLocale();
  const course = getCourse(courseSlug);
  const progress = useCourseProgressStore((s) => s.getProgress(courseSlug));

  // Same rule as the player's contents sheet, so the ticks agree.
  const slides = useMemo(() => (course ? getCourseSlides(course) : []), [course]);
  const visited = useMemo(
    () => new Set(progress?.completedSlides ?? []),
    [progress?.completedSlides],
  );

  if (!course) {
    return (
      <main className="px-4 py-10 text-center">
        <p className="text-body text-dark-gray">
          <FormattedMessage id="trainer.course.notFound" />
        </p>
        <Link
          to="/trainer/courses"
          className="mt-3 inline-block text-small font-medium text-primary"
        >
          <FormattedMessage id="trainer.course.backToCourses" />
        </Link>
      </main>
    );
  }

  const status = getCourseStatus(progress);
  const ctaId =
    status === "completed"
      ? "trainer.course.review"
      : status === "in-progress"
        ? "trainer.course.resume"
        : "trainer.course.start";

  return (
    <main className="pb-6">
      <div className="relative h-48 w-full">
        <img src={course.cover} alt="" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <h1 className="text-h1 font-medium text-white">
            {pickLocalized(course.title, locale)}
          </h1>
        </div>
      </div>

      <div className="space-y-6 px-4 pt-4">
        <div className="space-y-2">
          <CourseStatusChip status={status} />
          <p className="text-small text-dark-gray">
            {pickLocalized(course.subtitle, locale)}
          </p>
        </div>

        <div className="space-y-4">
          {course.topics.map((topic, topicIndex) => {
            const numbers = subtopicNumbers(topic, topicIndex);
            const canJump = status === "completed";
            return (
              <section key={topic.id} className="space-y-2">
                {canJump ? (
                  <Link
                    to="/trainer/courses/$courseSlug/player"
                    params={{ courseSlug: course.slug }}
                    search={{ slide: `${topic.id}/__cover` }}
                    className="block text-h3 font-medium text-black/70"
                  >
                    {topicNumber(topicIndex)}. {pickLocalized(topic.title, locale)}
                  </Link>
                ) : (
                  <h2 className="text-h3 font-medium text-black/70">
                    {topicNumber(topicIndex)}. {pickLocalized(topic.title, locale)}
                  </h2>
                )}
                <ul className="space-y-1.5 ps-2">
                  {topic.subtopics.map((subtopic, subtopicIndex) => {
                    const number = numbers[subtopicIndex];
                    // The outline stops at 5.4 — nested items like 5.4.1 are
                    // reached through their parent, not listed on their own.
                    if (subtopic.unnumbered || isNestedNumber(number)) return null;
                    const isDone = isSubtopicComplete(
                      slides,
                      visited,
                      topic.id,
                      subtopic.id,
                    );
                    const itemClassName = `flex items-start gap-2 text-small ${
                      isDone ? "text-primary" : "text-dark-gray"
                    }`;
                    const content = (
                      <>
                        <span
                          dir="ltr"
                          className={`shrink-0 tabular-nums ${
                            isDone ? "text-primary" : "text-gray"
                          }`}
                        >
                          {number}
                        </span>
                        <span className={`min-w-0 flex-1 ${canJump ? "underline" : ""}`}>
                          {pickLocalized(subtopic.title, locale)}
                        </span>
                        {isDone && (
                          <LuCheck size={14} className="mt-0.5 shrink-0" />
                        )}
                      </>
                    );
                    if (!canJump) {
                      return (
                        <li key={subtopic.id} className={itemClassName}>
                          {content}
                        </li>
                      );
                    }
                    const firstSlideId = slides.find((s) =>
                      s.slideId.startsWith(subtopicSlidePrefix(topic.id, subtopic.id)),
                    )?.slideId;
                    return (
                      <li key={subtopic.id}>
                        <Link
                          to="/trainer/courses/$courseSlug/player"
                          params={{ courseSlug: course.slug }}
                          search={{ slide: firstSlideId }}
                          className={`w-full text-start ${itemClassName}`}
                        >
                          {content}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </section>
            );
          })}
        </div>

        <Button
          className="w-full"
          onClick={() =>
            navigate({
              to: "/trainer/courses/$courseSlug/player",
              params: { courseSlug: course.slug },
            })
          }
        >
          <FormattedMessage id={ctaId} />
        </Button>
      </div>
    </main>
  );
}
